import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { projectInvitesApi } from '@/api/projectInvites';
import type {
  ProjectInvitesState,
  InviteResponse,
  CreateInviteLinkPayload,
  SendInviteEmailsPayload,
} from '@/types';
import { extractApiError } from '@/api/errorStatus';
import { rejectedApiError } from './rejectedApiError';

const initialState: ProjectInvitesState = {
  invites: [],
  isLoading: false,
  error: null,
};

export const fetchInvitesThunk = createAsyncThunk(
  'projectInvites/fetchAll',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await projectInvitesApi.list(projectId);
      const data = response.data as { invites: InviteResponse[] };
      return data.invites;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const createLinkThunk = createAsyncThunk(
  'projectInvites/createLink',
  async (
    { projectId, data }: { projectId: string; data: CreateInviteLinkPayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await projectInvitesApi.createLink(projectId, data);
      return response.data as InviteResponse;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const sendEmailsThunk = createAsyncThunk(
  'projectInvites/sendEmails',
  async (
    {
      projectId,
      data,
    }: { projectId: string; data: SendInviteEmailsPayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await projectInvitesApi.sendEmails(projectId, data);
      const result = response.data as { invites: InviteResponse[] };
      return result.invites;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const revokeInviteThunk = createAsyncThunk(
  'projectInvites/revoke',
  async (
    { projectId, inviteId }: { projectId: string; inviteId: string },
    { rejectWithValue },
  ) => {
    try {
      await projectInvitesApi.revoke(projectId, inviteId);
      return inviteId;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

export const deleteInviteThunk = createAsyncThunk(
  'projectInvites/delete',
  async (
    { projectId, inviteId }: { projectId: string; inviteId: string },
    { rejectWithValue },
  ) => {
    try {
      await projectInvitesApi.deleteInvite(projectId, inviteId);
      return inviteId;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

// WEB-1444: повторная отправка недоставленного письма. Успех приносит
// обновлённый инвайт (emailDeliveryFailed=false) — строка заменяется без
// перезагрузки списка, бейдж «Не доставлено» гаснет сразу.
export const resendInviteEmailThunk = createAsyncThunk(
  'projectInvites/resendEmail',
  async (
    { projectId, inviteId }: { projectId: string; inviteId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await projectInvitesApi.resendEmail(projectId, inviteId);
      return response.data as InviteResponse;
    } catch (error: unknown) {
      return rejectWithValue(
        extractApiError(error),
      );
    }
  },
);

const projectInvitesSlice = createSlice({
  name: 'projectInvites',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    // WEB-1337 P4: ProjectTeamCard dispatches this when the project
    // selection becomes empty (org has no projects, or mid-switch before a
    // project loads) so the PREVIOUS project's invites don't linger on
    // screen — a plain early-return in the loading effect left them stale.
    clearInvites(state) {
      state.invites = [];
    },
  },
  extraReducers: (builder) => {
    // fetchAll
    builder
      .addCase(fetchInvitesThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInvitesThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.invites = action.payload;
      })
      .addCase(fetchInvitesThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = rejectedApiError(action);
      });

    // createLink — prepend the new invite
    builder.addCase(createLinkThunk.fulfilled, (state, action) => {
      state.invites = [action.payload, ...state.invites];
    });

    // sendEmails — prepend the new invites
    builder.addCase(sendEmailsThunk.fulfilled, (state, action) => {
      state.invites = [...action.payload, ...state.invites];
    });

    // revoke — mark as revoked in place
    builder.addCase(revokeInviteThunk.fulfilled, (state, action) => {
      state.invites = state.invites.map((inv) =>
        inv.id === action.payload ? { ...inv, isRevoked: true } : inv,
      );
    });

    // delete — remove from list
    builder.addCase(deleteInviteThunk.fulfilled, (state, action) => {
      state.invites = state.invites.filter((inv) => inv.id !== action.payload);
    });

    // WEB-1444: resend — replace the row with the refreshed invite
    builder.addCase(resendInviteEmailThunk.fulfilled, (state, action) => {
      state.invites = state.invites.map((inv) =>
        inv.id === action.payload.id ? action.payload : inv,
      );
    });
  },
});

export const { clearError: clearInvitesError, clearInvites } = projectInvitesSlice.actions;
export default projectInvitesSlice.reducer;
